import React, { useEffect, useState, useCallback } from 'react';
import { Container, Table, Badge, Form, Button, Modal } from 'react-bootstrap';
import { usersApi } from '../../api/users';
import HrnbCard from '../../components/controls/HrnbCard';
import HrnbAlert from '../../components/controls/HrnbAlert';
import HrnbLoader from '../../components/controls/HrnbLoader';
import AdminPagination from '../components/pagination/AdminPagination';
import { UserRole } from '../utils/roles';
import './AdminUsers.css';

const AdminUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showRoleModal, setShowRoleModal] = useState(false);
    const [showBanModal, setShowBanModal] = useState(false);
    const [newRole, setNewRole] = useState('');
    const [banReason, setBanReason] = useState('');
    const [banDuration, setBanDuration] = useState(7);
    const [actionLoading, setActionLoading] = useState(false);

    // Пагинация и фильтры
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [roleFilter, setRoleFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    // Загрузка пользователей
    const loadUsers = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const params = {
                page: currentPage - 1,
                size: pageSize
            };
            if (roleFilter) params.role = roleFilter;
            if (statusFilter) params.status = statusFilter;

            const data = await usersApi.listAll(params);
            console.log('[AdminUsers] Raw API response:', data);

            let usersList = [];
            let total = 0;

            if (Array.isArray(data)) {
                usersList = data;
                total = data.length;
            } else if (data && Array.isArray(data.content)) {
                usersList = data.content;
                total = data.totalElements || data.total || data.content.length;
            } else {
                console.warn('[AdminUsers] Unexpected data format:', data);
            }

            console.log('[AdminUsers] Parsed users:', usersList);
            setUsers(usersList);
            setTotalItems(total);
        } catch (err) {
            console.error('[AdminUsers] Failed to load users:', err);
            if (err?.response?.status === 403) {
                setError('Доступ запрещён. Требуется роль ADMIN.');
            } else if (err?.response?.status === 500) {
                setError('Список пользователей временно недоступен.');
            } else {
                setError('Не удалось загрузить список пользователей');
            }
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, roleFilter, statusFilter]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    // Получение цвета бейджа роли
    const getRoleBadgeVariant = (role) => {
        switch (role) {
            case UserRole.ADMIN:
                return 'danger';
            case UserRole.MODERATOR:
                return 'warning';
            case UserRole.USER:
            default:
                return 'secondary';
        }
    };

    // Открытие модального окна смены роли
    const handleRoleChange = (user) => {
        setSelectedUser(user);
        setNewRole(user.role);
        setShowRoleModal(true);
    };

    // Сохранение новой роли
    const saveRole = async () => {
        if (!selectedUser) return;

        try {
            setActionLoading(true);
            await usersApi.update(selectedUser.id, {
                username: selectedUser.username,
                email: selectedUser.email,
                role: newRole
            });
            await loadUsers();
            setShowRoleModal(false);
            setSelectedUser(null);
        } catch (err) {
            setError(`Не удалось изменить роль пользователя: ${err.message}`);
            console.error('[AdminUsers] Failed to update role:', err);
        } finally {
            setActionLoading(false);
        }
    };

    // Удаление пользователя
    const handleDeleteUser = async (user) => {
        if (!window.confirm(`Вы уверены, что хотите удалить пользователя "${user.username}"?`)) {
            return;
        }

        try {
            setActionLoading(true);
            await usersApi.remove(user.id);
            await loadUsers();
        } catch (err) {
            setError(`Не удалось удалить пользователя: ${err.message}`);
            console.error('[AdminUsers] Failed to delete user:', err);
        } finally {
            setActionLoading(false);
        }
    };

    // Бан пользователя
    const handleBan = async () => {
        if (!selectedUser) return;
        if (!banReason.trim()) {
            setError('Укажите причину бана');
            return;
        }

        try {
            setActionLoading(true);
            await usersApi.ban(selectedUser.id, { reason: banReason.trim(), durationDays: banDuration });
            await loadUsers();
            setShowBanModal(false);
            setSelectedUser(null);
            setBanReason('');
            setBanDuration(7);
        } catch (err) {
            setError(`Не удалось забанить пользователя: ${err.message}`);
            console.error('[AdminUsers] Failed to ban user:', err);
        } finally {
            setActionLoading(false);
        }
    };

    // Разбан пользователя
    const handleUnban = async (user) => {
        if (!window.confirm(`Разбанить пользователя "${user.username}"?`)) {
            return;
        }

        try {
            setActionLoading(true);
            await usersApi.unban(user.id);
            await loadUsers();
        } catch (err) {
            setError(`Не удалось разбанить пользователя: ${err.message}`);
            console.error('[AdminUsers] Failed to unban user:', err);
        } finally {
            setActionLoading(false);
        }
    };

    const openBanModal = (user) => {
        setSelectedUser(user);
        setShowBanModal(true);
    };

    // Получение статуса пользователя
    const getUserStatus = (user) => {
        if (!user.isActive) return 'BANNED';
        return user.status || 'ACTIVE';
    };

    const getStatusBadgeVariant = (status) => {
        switch (status) {
            case 'ACTIVE': return 'success';
            case 'BANNED': return 'danger';
            case 'SUSPENDED': return 'warning';
            default: return 'secondary';
        }
    };

    if (loading) {
        return (
            <Container fluid className="admin-loading">
                <HrnbLoader />
            </Container>
        );
    }

    return (
        <Container fluid>
            <h1 className="admin-page-title">Управление пользователями</h1>

            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            <HrnbCard>
                <div className="admin-pagination-header">
                    <Form.Select
                        className="admin-page-size-selector"
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        <option value={5}>5 на странице</option>
                        <option value={10}>10 на странице</option>
                        <option value={25}>25 на странице</option>
                        <option value={50}>50 на странице</option>
                    </Form.Select>

                    <Form.Select
                        className="admin-filter-selector ms-2"
                        value={roleFilter}
                        onChange={(e) => {
                            setRoleFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                        style={{ width: 'auto' }}
                    >
                        <option value="">Все роли</option>
                        <option value="USER">USER</option>
                        <option value="MODERATOR">MODERATOR</option>
                        <option value="ADMIN">ADMIN</option>
                        <option value="UNCONFIRMED">UNCONFIRMED</option>
                    </Form.Select>

                    <Form.Select
                        className="admin-filter-selector ms-2"
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                        style={{ width: 'auto' }}
                    >
                        <option value="">Все статусы</option>
                        <option value="ACTIVE">ACTIVE</option>
                        <option value="BANNED">BANNED</option>
                        <option value="SUSPENDED">SUSPENDED</option>
                    </Form.Select>
                </div>

                <Table responsive hover className="admin-users-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Username</th>
                            <th>Email</th>
                            <th>Роль</th>
                            <th>Статус</th>
                            <th>Дата регистрации</th>
                            <th>Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="text-center text-muted">
                                    {error ? (
                                        <div>
                                            <div className="mb-2">⚠️ {error}</div>
                                        </div>
                                    ) : (
                                        'Пользователей не найдено'
                                    )}
                                </td>
                            </tr>
                        ) : (
                            users.map((user, index) => {
                                const userStatus = getUserStatus(user);
                                return (
                                    <tr key={user.id || user.username || index}>
                                        <td>{user.id}</td>
                                        <td>{user.username}</td>
                                        <td>{user.email}</td>
                                        <td>
                                            <Badge bg={getRoleBadgeVariant(user.role)}>
                                                {user.role}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Badge bg={getStatusBadgeVariant(userStatus)}>
                                                {userStatus}
                                            </Badge>
                                        </td>
                                        <td>
                                            {user.createdAt
                                                ? new Date(user.createdAt).toLocaleDateString('ru-RU')
                                                : '-'}
                                        </td>
                                        <td>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="me-2"
                                                onClick={() => handleRoleChange(user)}
                                                disabled={actionLoading}
                                            >
                                                Роль
                                            </Button>
                                            {userStatus === 'BANNED' ? (
                                                <Button
                                                    variant="outline-success"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => handleUnban(user)}
                                                    disabled={actionLoading}
                                                >
                                                    Разбан
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="outline-warning"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => openBanModal(user)}
                                                    disabled={actionLoading || user.role === UserRole.ADMIN}
                                                >
                                                    Бан
                                                </Button>
                                            )}
                                            {user.role !== UserRole.ADMIN && (
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => handleDeleteUser(user)}
                                                    disabled={actionLoading}
                                                >
                                                    Удалить
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </Table>

                <AdminPagination
                    currentPage={currentPage}
                    totalItems={totalItems}
                    pageSize={pageSize}
                    onPageChange={setCurrentPage}
                />
            </HrnbCard>

            {/* Модальное окно смены роли */}
            <Modal show={showRoleModal} onHide={() => setShowRoleModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Изменение роли пользователя</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Пользователь: <strong>{selectedUser?.username}</strong>
                    </p>
                    <Form>
                        <Form.Group controlId="roleSelect">
                            <Form.Label>Новая роль</Form.Label>
                            <Form.Select value={newRole} onChange={(e) => setNewRole(e.target.value)}>
                                <option value={UserRole.USER}>{UserRole.USER}</option>
                                <option value={UserRole.MODERATOR}>{UserRole.MODERATOR}</option>
                                <option value={UserRole.ADMIN}>{UserRole.ADMIN}</option>
                            </Form.Select>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRoleModal(false)}>
                        Отмена
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={saveRole}
                        disabled={actionLoading}
                    >
                        {actionLoading ? 'Сохранение...' : 'Сохранить'}
                    </Button>
                </Modal.Footer>
            </Modal>
            {/* Модальное окно бана */}
            <Modal show={showBanModal} onHide={() => setShowBanModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Бан пользователя</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Пользователь: <strong>{selectedUser?.username}</strong>
                    </p>
                    <Form>
                        <Form.Group controlId="banReason" className="mb-3">
                            <Form.Label>Причина</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Укажите причину бана"
                                value={banReason}
                                onChange={(e) => setBanReason(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group controlId="banDuration">
                            <Form.Label>Длительность (дней)</Form.Label>
                            <Form.Control
                                type="number"
                                min={1}
                                max={365}
                                value={banDuration}
                                onChange={(e) => setBanDuration(Number(e.target.value))}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowBanModal(false)}>
                        Отмена
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleBan}
                        disabled={actionLoading || !banReason.trim()}
                    >
                        {actionLoading ? 'Сохранение...' : 'Забанить'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AdminUsers;
